/**
 * Terugkeer-na-inloggen's open-redirect guard. A `redirect` value is only safe to navigate to
 * if it is unambiguously a relative path on this app's own origin - never something a browser
 * (or a lenient URL parser somewhere in the chain) could reinterpret as pointing elsewhere.
 *
 * Rejects backslashes and C0 control characters before even looking at the leading slash:
 * both are known browser-normalisation tricks for smuggling a protocol-relative `//` (or a
 * scheme) past a naive `startsWith('//')` check - e.g. a leading backslash (some browsers treat
 * it the same as `/`) or an embedded tab/newline (stripped by the browser, collapsing what
 * looked like a single slash into `//`). The same checks are re-run once against the
 * percent-decoded form, since those same tricks can also arrive URL-encoded.
 */
export function isSafeRedirectPath(path: string | null | undefined): path is string {
  if (!path) {
    return false;
  }
  if (!isRelativeOwnOriginPath(path)) {
    return false;
  }

  let decoded: string;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    // Malformed percent-encoding - not a path we can reason about safely.
    return false;
  }
  return isRelativeOwnOriginPath(decoded);
}

const BACKSLASH_CHAR_CODE = 92;

function isRelativeOwnOriginPath(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    // C0 control characters (0x00-0x1f) or a literal backslash anywhere in the value.
    if (code <= 0x1f || code === BACKSLASH_CHAR_CODE) {
      return false;
    }
  }
  if (!value.startsWith('/') || value.startsWith('//')) {
    return false;
  }
  return true;
}
